import { useCallback, useEffect, useRef, useState } from 'react';

import axios from 'axios';
import debounce from 'lodash.debounce';

import messages from '../constants/messages';

const API_URL = 'https://wft-geo-db.p.rapidapi.com';
const API_KEY = 'a3ff18f884msh2ebd1ffaa7c7ba7p118f7ejsnf8110f0d0ec7';
const DEBOUNCE_DELAY = 1000; // Rate Limit (1 Requests/Sec) in free plan

export const useCitySearch = () => {
  const [options, setOptions] = useState([]);
  const [nextPageHref, setNextPageHref] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cache = useRef(new Map());
  const cancelSource = useRef(null);

  const fetchCities = async (query, pageUrl = null, append = false) => {
    if (!pageUrl && !query?.trim()) {
      setOptions([]);

      return;
    }

    if (!append && cache.current.has(query)) {
      setOptions(cache.current.get(query));

      return;
    }

    setLoading(true);
    setError(null);

    cancelSource.current?.cancel('Previous request canceled');
    cancelSource.current = axios.CancelToken.source();

    try {
      const url = pageUrl || `${API_URL}/v1/geo/cities`;

      const resp = await axios.get(url, {
        cancelToken: cancelSource.current.token,
        headers: {
          'X-RapidAPI-Key': API_KEY,
          'X-RapidAPI-Host': 'wft-geo-db.p.rapidapi.com'
        },
        params: pageUrl
          ? {}
          : {
              types: 'CITY',
              languageCode: 'en',
              namePrefix: query
            }
      });

      const next = resp.data?.links?.find(l => l.rel === 'next');

      setNextPageHref(next?.href ?? null);

      const newItems =
        resp.data?.data?.map(c => ({
          label: `${c.name}, ${c.region}, ${c.country}`,
          value: `${c.name}, ${c.region}, ${c.country}`,
          key: `${c.name}-${c.region}-${c.country}`.toLowerCase(),
          city: c.name,
          region: c.region,
          country: c.country
        })) ?? [];

      setOptions(prev => {
        const merged = append ? [...prev, ...newItems] : newItems;
        const unique = new Map();

        for (const item of merged) {
          unique.set(item.key, item);
        }

        return Array.from(unique.values());
      });

      if (!append) {
        cache.current.set(query, newItems);
      }

      setTotalCount(resp.data?.metadata?.totalCount ?? 0);
    } catch (error) {
      if (axios.isCancel(error)) return;

      if (error.response?.status === 429) {
        setError(messages.RATE_LIMIT_REACHED);
      } else {
        setError(messages.FAILED_ON_LOAD_DATA('cities'));
      }
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useRef(debounce(q => fetchCities(q, null, false), DEBOUNCE_DELAY)).current;
  const debouncedLoadMore = useRef(debounce((query, url) => fetchCities(query, url, true), DEBOUNCE_DELAY)).current;

  const onSearch = useCallback(
    query => {
      setError(null);
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const loadMore = useCallback(
    query => {
      if (!nextPageHref) return;

      setError(null);
      debouncedLoadMore(query, `${API_URL}${nextPageHref}`);
    },
    [nextPageHref, debouncedLoadMore]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      debouncedLoadMore.cancel();
      cancelSource.current?.cancel();
    };
  }, []);

  return {
    loading,
    error,
    options,
    totalCount,
    hasMore: Boolean(nextPageHref),
    onSearch,
    loadMore
  };
};
